import React, { useState, useEffect } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import { useSocialAssets } from "../../../../hooks/useSocialAssets";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

const TWITTER_CHAR_LIMIT = 280;

const downloadMarkdown = (content: string, filename: string) => {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const CopyButton: React.FC<{ text: string; size?: "sm" | "md" }> = ({ text, size = "md" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      className="btn btn-sm"
      style={{
        padding: size === "sm" ? "3px 8px" : "6px 14px",
        fontSize: size === "sm" ? "11px" : "13px",
        fontWeight: 600,
        color: "#3B82F6",
        border: "1px solid #DBEAFE",
        borderRadius: size === "sm" ? "5px" : "7px",
        background: "transparent",
        flexShrink: 0,
      }}
      onClick={handleCopy}
    >
      <i className={`bi ${copied ? "bi-check2" : "bi-clipboard"} me-1`} />
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

// ── OG Description panel ────────────────────────────────────────────────────

const OGPanel: React.FC<{ text: string }> = ({ text }) => (
  <div className="py-3">
    <div
      style={{
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        borderRadius: "10px",
        padding: "14px 18px",
        marginBottom: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span style={{ fontSize: "13px", color: "#475569" }}>
        <i className="bi bi-link-45deg me-2 text-primary" />
        <strong>{text.length} chars</strong>
        {text.length > 160 && (
          <span style={{ color: "#F59E0B", marginLeft: "8px", fontSize: "12px" }}>
            <i className="bi bi-exclamation-triangle me-1" />
            Over 160 — may be truncated in link previews
          </span>
        )}
      </span>
      <CopyButton text={text} />
    </div>
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "18px",
        fontSize: "14px",
        color: "#334155",
        lineHeight: 1.7,
        whiteSpace: "pre-wrap",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {text}
    </div>
    {/* OG card preview mockup */}
    <div
      style={{
        marginTop: "16px",
        border: "1px solid #E2E8F0",
        borderRadius: "10px",
        overflow: "hidden",
        background: "#F1F5F9",
      }}
    >
      <div style={{ height: "80px", background: "linear-gradient(135deg, #DBEAFE, #EDE9FE)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className="bi bi-image" style={{ fontSize: "28px", color: "#94A3B8" }} />
      </div>
      <div style={{ padding: "10px 14px", borderTop: "1px solid #E2E8F0" }}>
        <div style={{ fontSize: "11px", color: "#94A3B8", marginBottom: "3px" }}>businessfactory.ai</div>
        <div style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A", marginBottom: "4px" }}>Link Preview</div>
        <div style={{ fontSize: "12px", color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {text}
        </div>
      </div>
    </div>
  </div>
);

// ── Twitter / X thread panel ─────────────────────────────────────────────────

const TwitterPanel: React.FC<{ tweets: string[]; projectTitle: string }> = ({ tweets, projectTitle }) => {
  const allText = tweets.map((t, i) => `${i + 1}/${tweets.length} ${t}`).join("\n\n---\n\n");

  return (
    <div className="py-3">
      <div
        style={{
          background: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "10px",
          padding: "14px 18px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: "13px", color: "#475569" }}>
          <i className="bi bi-twitter-x me-2 text-primary" />
          <strong>{tweets.length} tweets</strong> in thread
        </span>
        <div style={{ display: "flex", gap: "8px" }}>
          <CopyButton text={allText} />
          <button
            className="btn btn-sm"
            style={{
              padding: "6px 14px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#3B82F6",
              border: "1px solid #DBEAFE",
              borderRadius: "7px",
              background: "#FFFFFF",
            }}
            onClick={() =>
              downloadMarkdown(
                `# ${projectTitle} — Twitter/X Launch Thread\n\n` + allText,
                `${projectTitle.toLowerCase().replace(/\s+/g, "-")}-twitter-thread.md`
              )
            }
          >
            <i className="bi bi-file-earmark-arrow-down me-1" />
            Export .md
          </button>
        </div>
      </div>

      {tweets.map((tweet, idx) => {
        const charCount = tweet.length;
        const isOverLimit = charCount > TWITTER_CHAR_LIMIT;
        return (
          <div
            key={idx}
            style={{
              background: "#FFFFFF",
              border: `1px solid ${isOverLimit ? "#FCA5A5" : "#E2E8F0"}`,
              borderRadius: "12px",
              padding: "14px 18px",
              marginBottom: "10px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
              <div style={{ display: "flex", gap: "10px", flex: 1 }}>
                {/* Tweet number badge */}
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    background: "#DBEAFE",
                    color: "#3B82F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 700,
                    flexShrink: 0,
                    marginTop: "1px",
                  }}
                >
                  {idx + 1}
                </div>
                <div style={{ fontSize: "14px", color: "#334155", lineHeight: 1.65, flex: 1, whiteSpace: "pre-wrap" }}>
                  {tweet}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0 }}>
                <CopyButton text={tweet} size="sm" />
                {/* Char count indicator */}
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: isOverLimit ? "#EF4444" : charCount > 250 ? "#F59E0B" : "#94A3B8",
                  }}
                >
                  {charCount}/{TWITTER_CHAR_LIMIT}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Generic long-form text panel (LinkedIn, Facebook) ────────────────────────

const TextPanel: React.FC<{ text: string; platform: string; projectTitle: string; exportSlug: string }> = ({
  text,
  platform,
  projectTitle,
  exportSlug,
}) => (
  <div className="py-3">
    <div
      style={{
        background: "#F8FAFC",
        border: "1px solid #E2E8F0",
        borderRadius: "10px",
        padding: "14px 18px",
        marginBottom: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span style={{ fontSize: "13px", color: "#475569" }}>
        <strong>{text.length} chars</strong>
      </span>
      <div style={{ display: "flex", gap: "8px" }}>
        <CopyButton text={text} />
        <button
          className="btn btn-sm"
          style={{
            padding: "6px 14px",
            fontSize: "13px",
            fontWeight: 600,
            color: "#3B82F6",
            border: "1px solid #DBEAFE",
            borderRadius: "7px",
            background: "#FFFFFF",
          }}
          onClick={() =>
            downloadMarkdown(
              `# ${projectTitle} — ${platform}\n\n${text}`,
              `${projectTitle.toLowerCase().replace(/\s+/g, "-")}-${exportSlug}.md`
            )
          }
        >
          <i className="bi bi-file-earmark-arrow-down me-1" />
          Export .md
        </button>
      </div>
    </div>
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "18px",
        fontSize: "14px",
        color: "#334155",
        lineHeight: 1.75,
        whiteSpace: "pre-wrap",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {text}
    </div>
  </div>
);

// ── Instagram captions panel ─────────────────────────────────────────────────

const InstagramPanel: React.FC<{ captions: string[]; projectTitle: string }> = ({ captions, projectTitle }) => {
  const allText = captions.map((c, i) => `### Caption ${i + 1}\n\n${c}`).join("\n\n---\n\n");

  return (
    <div className="py-3">
      <div
        style={{
          background: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "10px",
          padding: "14px 18px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: "13px", color: "#475569" }}>
          <i className="bi bi-instagram me-2 text-primary" />
          <strong>{captions.length} caption variants</strong>
        </span>
        <div style={{ display: "flex", gap: "8px" }}>
          <CopyButton text={allText} />
          <button
            className="btn btn-sm"
            style={{
              padding: "6px 14px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#3B82F6",
              border: "1px solid #DBEAFE",
              borderRadius: "7px",
              background: "#FFFFFF",
            }}
            onClick={() =>
              downloadMarkdown(
                `# ${projectTitle} — Instagram Captions\n\n` + allText,
                `${projectTitle.toLowerCase().replace(/\s+/g, "-")}-instagram-captions.md`
              )
            }
          >
            <i className="bi bi-file-earmark-arrow-down me-1" />
            Export .md
          </button>
        </div>
      </div>

      {captions.map((caption, idx) => (
        <div
          key={idx}
          style={{
            background: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            padding: "14px 18px",
            marginBottom: "10px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
            <div style={{ display: "flex", gap: "10px", flex: 1 }}>
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #F9A8D4, #C084FC)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#FFFFFF",
                  flexShrink: 0,
                  marginTop: "1px",
                }}
              >
                {idx + 1}
              </div>
              <div style={{ fontSize: "14px", color: "#334155", lineHeight: 1.65, flex: 1, whiteSpace: "pre-wrap" }}>
                {caption}
              </div>
            </div>
            <CopyButton text={caption} size="sm" />
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Main modal ───────────────────────────────────────────────────────────────

const PLATFORM_TABS = [
  { key: "og", label: "OG / Link", icon: "bi-link-45deg", description: "Open Graph description for link previews" },
  { key: "twitter", label: "Twitter / X", icon: "bi-twitter-x", description: "Launch thread for Twitter/X" },
  { key: "linkedin", label: "LinkedIn", icon: "bi-linkedin", description: "Professional announcement post" },
  { key: "instagram", label: "Instagram", icon: "bi-instagram", description: "Caption variants for posts and carousel" },
  { key: "facebook", label: "Facebook", icon: "bi-facebook", description: "Facebook ad and post copy" },
] as const;

export const SocialAssetsModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const [activeTab, setActiveTab] = useState<string>("generate");
  const { isLoading, error, assets, generateAssets, clearError, reset } = useSocialAssets();

  useEffect(() => {
    if (show) {
      reset();
      setActiveTab("generate");
    }
  }, [show, reset]);

  const handleGenerate = async () => {
    await generateAssets(projectId);
    setActiveTab("og");
  };

  const handleExportAll = () => {
    if (!assets) return;
    const safeTitle = projectTitle.toLowerCase().replace(/\s+/g, "-");
    const sections = [
      `# ${projectTitle} — Social Assets\n`,
      `## OG / Link Preview\n\n${assets.ogDescription}`,
      `\n## Twitter / X Thread\n\n` +
        assets.twitterThread.map((t, i) => `${i + 1}/${assets.twitterThread.length} ${t}`).join("\n\n---\n\n"),
      `\n## LinkedIn Post\n\n${assets.linkedInPost}`,
      `\n## Instagram Captions\n\n` +
        assets.instagramCaptions.map((c, i) => `### Caption ${i + 1}\n\n${c}`).join("\n\n---\n\n"),
      `\n## Facebook Ad Copy\n\n${assets.facebookAdCopy}`,
    ];
    downloadMarkdown(sections.join("\n"), `${safeTitle}-social-assets.md`);
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static" scrollable>
      <Modal.Header closeButton style={{ border: "none", paddingBottom: 0 }}>
        <Modal.Title style={{ fontWeight: 700, color: "#0F172A", fontSize: "18px" }}>
          <i className="bi bi-share me-2 text-primary" />
          Social Asset Generator
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ paddingTop: "12px" }}>
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2" />
            <span className="flex-grow-1">{error}</span>
            <button type="button" className="btn-close" onClick={clearError} />
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mb-4"
        >
          {/* Generate tab */}
          <Tab eventKey="generate" title="Generate">
            <div className="py-5 text-center">
              <div className="mb-5">
                <div
                  style={{
                    width: "96px",
                    height: "96px",
                    background: "#DBEAFE",
                    borderRadius: "9999px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <i className="bi bi-share" style={{ fontSize: "40px", color: "#3B82F6" }} />
                </div>
                <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#0F172A", marginBottom: "8px" }}>
                  AI Social Assets
                </h3>
                <p style={{ fontSize: "14px", color: "#475569", maxWidth: "480px", margin: "0 auto" }}>
                  Generate ready-to-publish social content for{" "}
                  <strong>{projectTitle}</strong> — tailored to your validated
                  market, value proposition, and customer persona.
                </p>
              </div>

              <div
                style={{
                  background: "#DBEAFE",
                  borderRadius: "12px",
                  padding: "20px 24px",
                  maxWidth: "600px",
                  margin: "0 auto 32px",
                  textAlign: "left",
                }}
              >
                <h6 style={{ fontWeight: 700, color: "#1D4ED8", marginBottom: "12px" }}>
                  <i className="bi bi-magic me-2" />
                  What will be generated
                </h6>
                <div className="row">
                  {PLATFORM_TABS.map((p) => (
                    <div key={p.key} className="col-md-4 mb-2">
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                        <i className={`bi ${p.icon}`} style={{ color: "#3B82F6", marginTop: "2px" }} />
                        <div>
                          <div style={{ fontSize: "13px", fontWeight: 600, color: "#1E3A5F" }}>
                            {p.label}
                          </div>
                          <div style={{ fontSize: "11px", color: "#3B82F6" }}>{p.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="btn btn-primary btn-lg"
                style={{ padding: "12px 32px", fontWeight: 600, fontSize: "15px", borderRadius: "8px" }}
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Generating assets…
                  </>
                ) : (
                  <>
                    <i className="bi bi-lightning me-2" />
                    Generate Social Assets
                  </>
                )}
              </button>
            </div>
          </Tab>

          {/* Platform tabs — only available after generation */}
          {PLATFORM_TABS.map((platform) => (
            <Tab
              key={platform.key}
              eventKey={platform.key}
              title={
                <span>
                  <i className={`bi ${platform.icon} me-1`} />
                  {platform.label}
                </span>
              }
              disabled={!assets}
            >
              {assets && platform.key === "og" && <OGPanel text={assets.ogDescription} />}
              {assets && platform.key === "twitter" && (
                <TwitterPanel tweets={assets.twitterThread} projectTitle={projectTitle} />
              )}
              {assets && platform.key === "linkedin" && (
                <TextPanel
                  text={assets.linkedInPost}
                  platform="LinkedIn Post"
                  projectTitle={projectTitle}
                  exportSlug="linkedin-post"
                />
              )}
              {assets && platform.key === "instagram" && (
                <InstagramPanel captions={assets.instagramCaptions} projectTitle={projectTitle} />
              )}
              {assets && platform.key === "facebook" && (
                <TextPanel
                  text={assets.facebookAdCopy}
                  platform="Facebook Ad Copy"
                  projectTitle={projectTitle}
                  exportSlug="facebook-ad-copy"
                />
              )}
            </Tab>
          ))}
        </Tabs>
      </Modal.Body>

      <Modal.Footer style={{ border: "none", paddingTop: 0 }}>
        <button
          className="btn"
          style={{
            fontWeight: 600,
            fontSize: "14px",
            color: "#475569",
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            padding: "8px 18px",
          }}
          onClick={handleClose}
        >
          Close
        </button>
        {assets && (
          <>
            <button
              className="btn"
              style={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#3B82F6",
                border: "1px solid #DBEAFE",
                borderRadius: "8px",
                padding: "8px 18px",
                background: "transparent",
              }}
              onClick={handleExportAll}
            >
              <i className="bi bi-file-earmark-arrow-down me-1" />
              Export All Assets
            </button>
            <button
              className="btn btn-primary"
              style={{ fontWeight: 600, fontSize: "14px", padding: "8px 18px", borderRadius: "8px" }}
              onClick={handleGenerate}
              disabled={isLoading}
            >
              <i className="bi bi-arrow-clockwise me-1" />
              Regenerate
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
